import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

/**
 * Custom Hook to manage the status and pagination of
 * communes assigned to a specific dispatch area
 *
 * @param {number} zoneId - El ID de la zona de despacho
 */
export function useShippingZoneCommunes(zoneId) {
  // Data status and IU
  const [communes, setCommunes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 50,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search !== debouncedSearch) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search, debouncedSearch]);

  // STATE MANNERS (HANDLERS)
  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
  }, []);

  // FETCH DATA
  const fetchCommunes = useCallback(async () => {
    if (!zoneId) return;

    setLoading(true);
    setError(null);

    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
      search: debouncedSearch,
    };

    try {
      const { data } = await axios.get(`shipping_zones/${zoneId}/communes/`, {
        params,
      });
      setCommunes(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error(`Error obteniendo las comunas de la zona: ${err}`);
      setError("No se pudieron cargar las comunas de esta zona.");
    } finally {
      setLoading(false);
    }
  }, [zoneId, pagination.page, pagination.rowsPerPage, debouncedSearch]);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    fetchCommunes();
  }, [fetchCommunes]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    communes,
    loading,
    error,
    search,
    totalCount,
    pagination,

    // MANNERS (Setters y Actions)
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    refetchCommunes: fetchCommunes,
  };
}
